"use client";

import { useEffect, useState } from "react";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { m, useReducedMotion } from "framer-motion";
import { useCart } from "@/contexts/CartContext";
import { cn, formatPrice } from "@/lib/shared/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import EditableText from "@/components/edit-mode/EditableText";

export const CART_OPEN_EVENT = "monochrome:cart:open";

interface LiveCartProps {
  compact: boolean;
}

export default function LiveCart({ compact }: LiveCartProps) {
  const { cart, total } = useCart();
  const [mounted, setMounted] = useState(false);
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();

  useEffect(() => {
    setMounted(true);
  }, []);

  const showCart = mounted && cart.length > 0;
  const itemCount = cart.reduce((acc, item) => acc + item.quantity, 0);
  const totalLabel = formatPrice(total);

  const open = () => {
    window.dispatchEvent(new CustomEvent(CART_OPEN_EVENT));
  };

  if (!showCart) {
    return (
      <button
        type="button"
        onClick={open}
        aria-label="Open cart"
        className="text-foreground hover:text-foreground/60 group relative -m-2 inline-flex cursor-pointer items-center gap-2 p-2 transition-colors"
      >
        <ShoppingBag
          size={isMobile ? 18 : 20}
          strokeWidth={1.5}
          className="transition-transform group-hover:scale-110"
        />
        {!isMobile && !compact && (
          <span className="text-foreground/40 group-hover:text-foreground/70 hidden font-mono text-[10px] font-bold tracking-[0.2em] uppercase transition-colors md:inline">
            <EditableText section="cart" field="title" value="Bag" />
          </span>
        )}
      </button>
    );
  }

  if (isMobile) {
    return (
      <m.button
        type="button"
        onClick={open}
        aria-label={`Open cart — ${itemCount} ${itemCount === 1 ? "item" : "items"}, total ${totalLabel}`}
        initial={reducedMotion ? false : { opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 380, damping: 28 }}
        className="text-foreground hover:text-foreground/60 group inline-flex cursor-pointer items-center gap-1.5 transition-colors"
      >
        <ShoppingBag
          size={18}
          strokeWidth={1.5}
          className="transition-transform group-hover:scale-110"
        />
        <span
          aria-hidden
          className="border-foreground text-foreground inline-flex h-4 min-w-4 items-center justify-center border px-1 font-mono text-[10px] font-bold tabular-nums"
        >
          <m.span
            key={itemCount}
            initial={reducedMotion ? false : { y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 28 }}
            className="inline-block"
          >
            {itemCount}
          </m.span>
        </span>
      </m.button>
    );
  }

  return (
    <m.button
      type="button"
      onClick={open}
      aria-label={`Open cart — ${itemCount} ${itemCount === 1 ? "item" : "items"}, total ${totalLabel}`}
      initial={reducedMotion ? false : { opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 380, damping: 28 }}
      className={cn(
        "group bg-foreground text-background hover:bg-foreground/90 border-foreground inline-flex cursor-pointer items-center gap-2 border transition-colors",
        compact ? "px-2 py-1 text-[10px]" : "px-3 py-1.5 text-xs"
      )}
    >
      <ShoppingBag size={compact ? 12 : 14} strokeWidth={1.75} />
      {!compact && (
        <span className="font-mono font-bold tracking-[0.18em] uppercase">
          <EditableText section="cart" field="title" value="Bag" />
        </span>
      )}
      <span
        aria-hidden
        className="bg-background text-foreground inline-flex h-4 min-w-4 items-center justify-center px-1 font-mono text-[10px] font-bold tabular-nums"
      >
        <m.span
          key={itemCount}
          initial={reducedMotion ? false : { y: -8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 28 }}
          className="inline-block"
        >
          {itemCount}
        </m.span>
      </span>
      {!compact && (
        <>
          <span aria-hidden className="bg-background/30 block h-3 w-px" />
          <span className="font-mono font-bold tracking-[0.1em] tabular-nums">{totalLabel}</span>
          <ArrowRight
            size={12}
            strokeWidth={2}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </>
      )}
    </m.button>
  );
}
